import React, { useState } from "react";
import { Ruler } from "lucide-react";

const SPECS = { circumference: 59.05, height: 8.89 };
const fmt = (cm, imperial) => (imperial ? `${(cm / 2.54).toFixed(2)} in` : `${cm.toFixed(2)} cm`);

const FEATURES = [
  ["100% cotton", "Specially spun fibres make a very strong, smooth fabric — perfect for crisp printing."],
  ["Structured mid profile", "Six-panel cap with a contoured crown and curved peak for a refined, versatile shape."],
  ["Plastic snapback", "Adjustable closure so one size fits most adult heads."],
  ["Tonal under-peak & eyelets", "Tonal under-peak lining with breathable eyelets to keep things cool."],
];

export default function CapSizeGuide() {
  const [imperial, setImperial] = useState(false);
  return (
    <div data-testid="cap-size-guide">
      <div className="bg-white rounded-3xl soft-shadow overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-[#FFD93D]/30">
          <p className="font-display font-bold text-[#19324A] text-sm">Unisex Adults Icon Cap · One size</p>
          <div className="flex rounded-full bg-white p-0.5 text-xs font-display font-bold" role="group" aria-label="Units">
            {[["Metric", false], ["Imperial", true]].map(([label, v]) => (
              <button key={label} type="button" onClick={() => setImperial(v)} data-testid={`cap-units-${label.toLowerCase()}`}
                className={`px-3 py-1 rounded-full transition-colors ${imperial === v ? "bg-[#19324A] text-white" : "text-[#19324A]/70 hover:text-[#19324A]"}`}>{label}</button>
            ))}
          </div>
        </div>
        <table className="w-full text-left">
          <thead><tr className="border-t border-[#19324A]/8">{["Size", "Circumference", "Crown height"].map((c) => <th key={c} className="px-4 py-3 font-display font-bold text-[#19324A] text-sm">{c}</th>)}</tr></thead>
          <tbody>
            <tr className="border-t border-[#19324A]/8">
              <td className="px-4 py-3 text-[#19324A]/80 text-sm font-semibold">One size (adjustable)</td>
              <td className="px-4 py-3 text-[#19324A]/80 text-sm" data-testid="cap-circumference">{fmt(SPECS.circumference, imperial)}</td>
              <td className="px-4 py-3 text-[#19324A]/80 text-sm" data-testid="cap-height">{fmt(SPECS.height, imperial)}</td>
            </tr>
          </tbody>
        </table>
        <p className="px-4 pb-4 text-xs text-[#19324A]/50">Measurements refer to the product itself. The snapback adjusts to fit most adults (roughly 54–62 cm heads).</p>
      </div>

      <div className="mt-5 bg-[#4CC9F0]/15 rounded-3xl p-5 text-[#19324A]/80">
        <p className="font-display font-bold text-[#19324A] mb-2 inline-flex items-center gap-2"><Ruler className="w-4 h-4" /> How to measure your head</p>
        <ul className="list-disc list-inside space-y-1 text-sm">
          <li>Wrap a soft tape measure around your head about 1 cm above your ears and eyebrows — where the cap will sit.</li>
          <li>Keep the tape snug but not tight, and read the measurement at the front.</li>
          <li>No tape? Use a piece of string, then lay it against a ruler.</li>
        </ul>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-3xl soft-shadow p-5">
          <p className="font-display font-bold text-[#19324A] mb-2">About the Icon Cap</p>
          <p className="text-sm text-[#19324A]/80">A polished, mid-profile snapback with a contoured crown and curved peak. Structured six-panel build with a tonal under-peak and breathable eyelets — made for everyday Chewie &amp; Solo adventures.</p>
          <dl className="mt-3 text-xs text-[#19324A]/60 space-y-1">
            <div className="flex gap-2"><dt className="font-semibold text-[#19324A]/80">Age:</dt><dd>For adults</dd></div>
            <div className="flex gap-2"><dt className="font-semibold text-[#19324A]/80">Blank sourced from:</dt><dd>Honduras</dd></div>
          </dl>
        </div>
        <div className="bg-white rounded-3xl soft-shadow p-5">
          <p className="font-display font-bold text-[#19324A] mb-2">Key features</p>
          <ul className="space-y-2 text-sm text-[#19324A]/80">
            {FEATURES.map(([t, d]) => <li key={t}><span className="font-semibold text-[#19324A]">{t}</span> — {d}</li>)}
          </ul>
        </div>
      </div>

      <div className="mt-5 bg-[#FF6B9D]/10 rounded-3xl p-5 text-[#19324A]/80" data-testid="cap-care">
        <p className="font-display font-bold text-[#19324A] mb-2">Care instructions</p>
        <p className="text-sm">Do not bleach. Spot clean with warm water and a little dish soap — there's no need to soak the whole cap. For stubborn spots, use a soft-bristled brush.</p>
      </div>
    </div>
  );
}
